import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { useIssueView } from "#app/components/issue-view";
import type { EntityKind } from "#app/lib/entity-visuals";

// Lives in app-providers.tsx, above every overlay host (so portaled overlay
// content can call it), and renders NO dialog inline — split-context rule.
// EntityDetailDialogs reads `active`. Routing: `issue` delegates to the
// issue-view overlay; every other kind sets `active` for the dialog host.

export type ActiveEntityDetail = {
  kind: Exclude<EntityKind, "issue">;
  id: string;
};

export interface EntityDetailContextValue {
  active: ActiveEntityDetail | null;
  openEntity: (kind: EntityKind, id: string) => void;
  closeEntity: () => void;
}

const EntityDetailContext = createContext<EntityDetailContextValue | null>(null);

export function EntityDetailProvider({ children }: { children: React.ReactNode }) {
  const { openIssueById } = useIssueView();
  const [active, setActive] = useState<ActiveEntityDetail | null>(null);

  const openEntity = useCallback(
    (kind: EntityKind, id: string) => {
      if (kind === "issue") {
        openIssueById(id);
        return;
      }
      setActive({ kind, id });
    },
    [openIssueById],
  );

  const closeEntity = useCallback(() => setActive(null), []);

  const value = useMemo(
    () => ({ active, openEntity, closeEntity }),
    [active, openEntity, closeEntity],
  );

  return (
    <EntityDetailContext.Provider value={value}>
      {children}
    </EntityDetailContext.Provider>
  );
}

export function useEntityDetail(): EntityDetailContextValue {
  const ctx = useContext(EntityDetailContext);
  if (!ctx) {
    throw new Error("useEntityDetail must be used within EntityDetailProvider");
  }
  return ctx;
}

export function useEntityDetailOptional(): EntityDetailContextValue | undefined {
  return useContext(EntityDetailContext) ?? undefined;
}
